import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, type User } from "./api.js";

interface AuthState {
  user: User | null;
  authProvider: "dev" | "google";
  /** Operator-set banner text; "" on production. */
  deploymentLabel: string;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authProvider, setAuthProvider] = useState<"dev" | "google">("dev");
  const [deploymentLabel, setDeploymentLabel] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: User; authProvider: "dev" | "google" }>("/api/me");
      setUser(res.user);
      setAuthProvider(res.authProvider);
    } catch (err) {
      // 401 is the normal signed-out state, not an error worth surfacing.
      if (!(err instanceof ApiError && err.status === 401)) console.error(err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Read deployment facts separately, because `/api/me` 401s when signed out and
   * the sign-in page needs both: which provider button to draw, and whether to
   * warn that this is not production. Runs once -- neither value changes without
   * a server restart.
   */
  const loadMeta = useCallback(async () => {
    try {
      const meta = await api.get<{ authProvider: "dev" | "google"; deploymentLabel: string }>(
        "/api/meta"
      );
      setAuthProvider(meta.authProvider);
      setDeploymentLabel(meta.deploymentLabel);
    } catch (err) {
      // Leave the defaults; the sign-in page still works.
      console.error(err);
    }
  }, []);

  useEffect(() => {
    void loadMeta();
    void refresh();
  }, [loadMeta, refresh]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, authProvider, deploymentLabel, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
